import { getExternalPatientRoutine, getExternalPatientRoutinePromiseCreator } from '../redux/Patients/routines'
import { externalLinkLoading } from '../redux/Patients/selectors'
import { alertRoutine } from '../redux/Alerts/routines'
import { localeIsInitialized } from '../i18n';
import moment from 'moment';
import { v4 as uuidv4} from 'uuid';
import { isLoggedInSelector } from '../redux/Auth/selectors';
import ajv from './ajvInstance'
import { externalPatientSchema } from '../components/Patient/forms/patientSchema'

const validateExternalPatient = ajv.compile(externalPatientSchema);
const PENDING_EXTERNAL_OPEN_STORAGE_KEY = 'cephalomax.pending_external_open';

const isExternalOpenType = (type) => type === 'doctolib_open' || type === 'generic_open';

const readPendingExternalOpen = () => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PENDING_EXTERNAL_OPEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data || !parsed.data.type) return null;
    return parsed;
  } catch (error) {
    console.warn('pending external open parse failed', error);
    return null;
  }
};

const writePendingExternalOpen = (data) => {
  if (typeof sessionStorage === 'undefined') return null;
  if (!data || !isExternalOpenType(data.type)) return null;
  const payload = {
    data,
    savedAt: Date.now(),
  };
  try {
    sessionStorage.setItem(PENDING_EXTERNAL_OPEN_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('pending external open save failed', error);
  }
  return payload;
};

const clearPendingExternalOpen = () => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(PENDING_EXTERNAL_OPEN_STORAGE_KEY);
  } catch (error) {
    console.warn('pending external open clear failed', error);
  }
};

const getExternalOpenPatientLabel = (data) => {
  if (!data || typeof data !== 'object') return '';
  const patient = data.patient || {};
  let firstname = '';
  let lastname = '';
  if (data.type === 'doctolib_open') {
    firstname = patient.first_name || '';
    lastname = patient.last_name || patient.maiden_name || '';
  } else {
    firstname = patient.firstname || patient.first_name || '';
    lastname = patient.lastname || patient.last_name || '';
  }
  return `${firstname} ${lastname}`.trim();
};

function mapDoctolibToCephalo(p) {
  if (!p || typeof p !== 'object') return {};
  const trim = (s) => (typeof s === 'string' ? s.trim() : s);
  const sex = (p.biological_sex || '').toString().toLowerCase();
  const gender = sex === 'female' ? 'female' : sex === 'male' ? 'male' : undefined;
  const civilite = gender === 'female' ? 'Madame' : 'Monsieur';
  const dateofbirth =  p.birthdate instanceof Date ? p.birthdate.toISOString().slice(0, 10) : (p.birthdate || '').slice(0, 10);
  const phone = trim(p.phone_number || p.fallback_phone_number || '');
  const email = trim(p.email || '');
  const adresse1 = trim(p.address || '');
  const adresse2 = trim(p.address_supplement || '');
  const postcode = trim(p.zipcode || '');
  const city = trim(p.city || '');

  const patient = {
    civilite,
    firstname: trim(p.first_name || ''),
    lastname: trim(p.last_name || p.maiden_name || ''),
    gender: gender, // default to keep schema satisfied if missing
    dateofbirth,
    email,
    phone,
    adresse1,
    adresse2,
    postcode,
    city,
    healthcare_id: trim(p.national_health_identifier || ''),
    // optional extras
  };

  // also include assure_* only if you can map them from doctype; default leave absent
  return patient;
}

export default (store, i18n) => {

  const isInitialized = localeIsInitialized(i18n)
  let lastPendingPromptAt = null;
  let wasLoggedIn = isLoggedInSelector(store.getState());

  const handleDoctolibOpen = (data) => {
    const rawExternalId = data?.patient?.id;
    const externalId = rawExternalId ? String(rawExternalId).split('-')[0] : null;
    const incomingPatient = mapDoctolibToCephalo(data?.patient)

    if (!incomingPatient) {
      console.warn('doctolib_open payload missing patient data', data)
      return;
    }

    const isValid = validateExternalPatient(incomingPatient)

    if (!isValid) {
      console.warn('doctolib_open payload did not pass validation', validateExternalPatient.errors, incomingPatient)
      return;
    }

    const normalizedPatient = {
      ...incomingPatient,
      collect_accept: true,
      is_patient_assure: incomingPatient.is_patient_assure ?? true,
    }

    if (!externalId) {
      console.warn('doctolib_open payload missing external identifier', data)
      return;
    }

    const service = 'doctolib'

    const patient = {
      ...normalizedPatient,
      external_ids: [{ service, id: `${externalId}` }],
    }

    store.dispatch(getExternalPatientRoutine.trigger({
      service,
      id: `${externalId}`,
      patient,
      silent: false,
    }))
  };

  const handleGenericOpen = (data, options = {}) => {
    const { patient: incomingPatient, software_name: softwareName, external_id: externalId, silent } = data || {}

    if (!incomingPatient) {
      console.warn('generic_open payload missing patient data', data)
      return;
    }

    const isValid = validateExternalPatient(incomingPatient)

    if (!isValid) {
      console.warn('generic_open payload did not pass validation', validateExternalPatient.errors, incomingPatient)
      return;
    }

    const normalizedPatient = {
      ...incomingPatient,
      collect_accept: true,
      is_patient_assure: incomingPatient.is_patient_assure ?? true,
    }

    if (!externalId) {
      console.warn('generic_open payload missing external identifier', data)
      return;
    }

    const service = softwareName || 'generic'
    const effectiveSilent = typeof options.silentOverride === 'boolean' ? options.silentOverride : silent;

    const patient = {
      ...normalizedPatient,
      external_ids: [{ service, id: `${externalId}` }],
    }

    store.dispatch(getExternalPatientRoutine.trigger({
      service,
      id: `${externalId}`,
      patient,
      silent: effectiveSilent,
    }))
  };

  const handleExternalOpen = (data, options = {}) => {
    if (!data || !data.type) return false;
    if (data.type === 'doctolib_open') {
      handleDoctolibOpen(data);
      return true;
    }
    if (data.type === 'generic_open') {
      handleGenericOpen(data, options);
      return true;
    }
    return false;
  };

  const maybeHandlePendingExternalOpen = () => {
    const pending = readPendingExternalOpen();
    if (!pending) return;
    if (pending.savedAt && pending.savedAt === lastPendingPromptAt) return;
    lastPendingPromptAt = pending.savedAt || Date.now();

    if (pending.data?.silent) {
      clearPendingExternalOpen();
      handleExternalOpen(pending.data);
      return;
    }

    const patientLabel = getExternalOpenPatientLabel(pending.data);
    const nameSuffix = patientLabel ? ` (${patientLabel})` : '';

    store.dispatch(alertRoutine.show({
      type: 'dialog',
      maxWidth: 'xs',
      title: i18n.t('common:interface.external link pending title'),
      content: i18n.t('common:interface.external link pending content', { name: nameSuffix }),
      actionTitle: i18n.t('common:button.ok'),
      action: () => {
        clearPendingExternalOpen();
        handleExternalOpen(pending.data);
        return true;
      },
      reject: () => {
        clearPendingExternalOpen();
      }
    }))
  };

  window.addEventListener("message", async (e) => {
    if (e.origin !== window.origin)
      return;

    const { type } = e.data || {};
    const isLoggedIn = isLoggedInSelector(store.getState());

    if (isLoggedIn === false) {
      if (isExternalOpenType(type)) {
        writePendingExternalOpen(e.data);
      }
      return;
    }

    if (type === 'doctolib_get_next_appointment') {

      let response = {
        delay : new Date(),
        delay_name: undefined,
        visit_motive_id: undefined,
        next_appointment_type: undefined,
      }
      try {
        const {entities: {contacts = {}}} = await getExternalPatientRoutinePromiseCreator({
          service: 'doctolib',
          silent: true,
          id: e.data.patient.id.split('-')[0],
          patient: e.data.patient
        }, store.dispatch)
        const last_contact = Object.values(contacts).filter(c => c.date && !c.type).sort((a,b) => a.date < b.date ? 1 : a.date > b.date ? -1 : a.createdAt < b.createdAt ? 1 : -1 )[0]
        if(last_contact) {
          //on récupère les donnée de délai et de type de rdv
          if(last_contact.delai_time_unit && last_contact.delai_number) {
            response.delay = moment().add(last_contact.delai_number, last_contact.delai_time_unit).toDate()
            response.delay_name = last_contact.delai
          }
          if(last_contact.next_appointment_type_external_id) {
            response.visit_motive_id = parseInt(last_contact.next_appointment_type_external_id)
          }
          if(last_contact.next_appointment_type) {
            response.next_appointment_type = last_contact.next_appointment_type
          }
        }
      } finally {
        e.source.postMessage({
          id: e.data.id,
          type: 'response_doctolib_get_next_appointment',
          ...response,
        })
      }
      return;
    }

    //legacy support of old extension version for most used link doctolib
    if (type === 'doctolib_open') {
      handleDoctolibOpen(e.data);
      return;
    }

    if (type !== 'generic_open')
      return;

    handleGenericOpen(e.data);
  }, false);
  
  let alertId;
  
  store.subscribe(async () => {

    await isInitialized
    const state = store.getState();
    const isLoggedIn = isLoggedInSelector(state);

    if (isLoggedIn && (!wasLoggedIn || lastPendingPromptAt === null)) {
      maybeHandlePendingExternalOpen();
    }

    if (!isLoggedIn && wasLoggedIn) {
      lastPendingPromptAt = null;
    }
    wasLoggedIn = isLoggedIn;

    const showExternalLinkLoading = externalLinkLoading(state)
    
    if(showExternalLinkLoading) {
      if(!alertId) {
        alertId = uuidv4()
        store.dispatch(alertRoutine.show({
          id: alertId,
          type: 'snack',
          title: i18n.t('common:interface.external link loading'),
          persist: true,
          closeReasons: [],
          loading: true,
        }))
      }
      return
    }



    if(alertId) {
      const action = alertRoutine.hide(alertId)
      alertId = null
      store.dispatch(action)
    }
    
  })
}
